import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Input from "../Components/Input";

const attendanceOptions = [
  { label: "0 - 100", value: "100" },
  { label: "100 - 500", value: "500" },
  { label: "500 - 1,000", value: "1000" },
  { label: "1,000 - 5,000", value: "5000" },
  { label: "5,000 - 10,000", value: "10000" },
  { label: "10,000 - 50,000", value: "50000" },
  { label: "50,000 - 100,000", value: "100000" },
  { label: "100,000 +", value: "max" },
];

const attendanceMap = {
  "100":    [null, 100],
  "500":    [100, 500],
  "1000":   [500, 1000],
  "5000":   [1000, 5000],
  "10000":  [5000, 10000],
  "50000":  [10000, 50000],
  "100000": [50000, 100000],
  "max":    [100000, null],
};

const filterByOptions = ["Attendance", "Gross Sales"];

const ComparisonFilter = ({
  setAttendanceMin,
  setAttendanceMax,
  setAttendanceMinAll,
  setAttendanceMaxAll,
  setGrossMin,
  setGrossMax,
  setGrossMinAll,
  setGrossMaxAll,
  resetPagination,
  setCurrentPage,
}) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [filterBy, setFilterBy] = useState("");
  const [hasAttendanceFilter, setHasAttendanceFilter] = useState(false);
  const [hasGrossFilter, setHasGrossFilter] = useState(false);
  const [attendanceValue, setAttendanceValue] = useState(null);
  const [grossMinInput, setGrossMinInput] = useState("");
  const [grossMaxInput, setGrossMaxInput] = useState("");

  const dropDownRef = useRef();

  const handleSetFilterBy = (e) => {
    setFilterBy(e.target.textContent);
  };

  const handleAttendanceSelect = (option) => {
    setAttendanceValue(option);
    if (!option) {
      setHasAttendanceFilter(false);
      setAttendanceMin(null);
      setAttendanceMax(null);
      setAttendanceMinAll(null);
      setAttendanceMaxAll(null);
      resetPagination();
      setCurrentPage(1);
      return;
    }
    setHasAttendanceFilter(true);
    const [min, max] = attendanceMap[option.value];
    setAttendanceMin(min);
    setAttendanceMax(max);
    setAttendanceMinAll(min);
    setAttendanceMaxAll(max);
    resetPagination();
    setCurrentPage(1);
  };

  const applyGrossFilter = () => {
    const min = grossMinInput !== "" ? parseFloat(grossMinInput) : null;
    const max = grossMaxInput !== "" ? parseFloat(grossMaxInput) : null;
    if ((min !== null && isNaN(min)) || (max !== null && isNaN(max))) return;
    setHasGrossFilter(min !== null || max !== null);
    setGrossMin(min);
    setGrossMax(max);
    setGrossMinAll(min);
    setGrossMaxAll(max);
    resetPagination();
    setCurrentPage(1);
  };

  const clear = () => {
    setFilterBy("");
    setHasAttendanceFilter(false);
    setHasGrossFilter(false);
    setAttendanceValue(null);
    setAttendanceMin(null);
    setAttendanceMax(null);
    setAttendanceMinAll(null);
    setAttendanceMaxAll(null);
    setGrossMin(null);
    setGrossMax(null);
    setGrossMinAll(null);
    setGrossMaxAll(null);
    setGrossMinInput("");
    setGrossMaxInput("");
    resetPagination();
    setCurrentPage(1);
  };

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (showDropDown && dropDownRef.current && !dropDownRef.current.contains(e.target)) {
        setShowDropDown(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => document.removeEventListener("mousedown", checkIfClickedOutside);
  }, [showDropDown]);

  const isActive = hasAttendanceFilter || hasGrossFilter;

  const buttonLabel = () => {
    const active = [];
    if (hasAttendanceFilter) active.push("Attendance");
    if (hasGrossFilter) active.push("Gross");
    if (active.length === 0) return "Filter";
    return `${active.join(" & ")} filter active`;
  };

  return (
    <div className="flex flex-col w-1/4 ml-2 relative" ref={dropDownRef}>
      <span className="text-xs text-black">FILTER BY</span>
      <button
        className={`border text-left text-xs h-9 rounded-md ${
          isActive ? "border-blue-500 text-blue-600" : "border-gray-300 text-gray-500"
        }`}
        onClick={() => setShowDropDown(!showDropDown)}
      >
        <div className="flex justify-between items-center h-full">
          <span className="pl-2">{buttonLabel()}</span>
          <span className="bg-gray-100 h-full px-2 flex items-center rounded-r-md border-l border-gray-300">
            <FontAwesomeIcon className="text-xs" icon={faArrowDown} />
          </span>
        </div>
      </button>
      {showDropDown && (
        <div className="absolute top-full mt-1 min-w-64 z-10 bg-white border shadow-xl rounded-md p-3 text-xs text-black font-normal">
          <div className="flex justify-between mb-2">
            <div>FILTER BY</div>
            {(isActive || filterBy) && (
              <button className="text-blue-500 italic" onClick={clear}>
                Clear all
              </button>
            )}
          </div>
          <div className="flex mb-3">
            {filterByOptions.map((option, i) => (
              <button
                key={i}
                className={
                  filterBy === option
                    ? "bg-blue-500 text-white rounded p-1 mr-2"
                    : "border border-blue-500 text-blue-500 rounded p-1 mr-2"
                }
                onClick={handleSetFilterBy}
              >
                {option}
              </button>
            ))}
          </div>
          {filterBy === "Attendance" && (
            <Select
              id="attendance_filter"
              className="text-xs font-light"
              placeholder="All attendance..."
              options={attendanceOptions}
              value={attendanceValue}
              onChange={handleAttendanceSelect}
              isClearable
            />
          )}
          {filterBy === "Gross Sales" && (
            <div>
              <div className="flex gap-2">
                <Input
                  name="gross_min"
                  type="number"
                  placeholder="Min ($)"
                  value={grossMinInput}
                  onChange={(e) => setGrossMinInput(e.target.value)}
                />
                <Input
                  name="gross_max"
                  type="number"
                  placeholder="Max ($)"
                  value={grossMaxInput}
                  onChange={(e) => setGrossMaxInput(e.target.value)}
                />
              </div>
              <button
                className="mt-2 bg-blue-500 text-white rounded px-3 py-1"
                onClick={applyGrossFilter}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparisonFilter;
